/**
 * Scorecard Organization Tree — Admin Panel sub-tab.
 *
 * Editable hierarchical view of Company → PM/Division → IB/Channel → STAFF.
 * Add/edit/delete/reorder nodes; auto-link STAFF to CRM user; auto-link IB to CRM channel.
 *
 * Replaces the external scorecard-builder-2.emergent.host org/scorecard page.
 */
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  ChevronDown, ChevronRight, Plus, Edit2, Trash2, Building2, Briefcase,
  Users, User, Link2, RefreshCw, AlertCircle, Search, Wand2, Copy,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_META = {
  COMPANY: { label: 'Company',  icon: Building2, color: '#10B981', childType: 'PM' },
  PM:      { label: 'Division', icon: Briefcase, color: '#3B82F6', childType: 'IB' },
  IB:      { label: 'Channel',  icon: Users,     color: '#F59E0B', childType: 'STAFF' },
  STAFF:   { label: 'Staff',    icon: User,      color: '#8B5CF6', childType: null },
};

function NodeRow({ node, depth, expanded, onToggle, onAdd, onEdit, onDelete, onDuplicate, hasChildren }) {
  const meta = TYPE_META[node.type] || TYPE_META.STAFF;
  const Icon = meta.icon;
  const canHaveChildren = meta.childType !== null;
  return (
    <div
      className="group flex items-center gap-2 px-3 py-2 rounded-md hover:bg-[hsl(var(--surface-elevated))] transition"
      style={{ paddingLeft: `${depth * 20 + 12}px` }}
      data-testid={`org-node-row-${node.id}`}
    >
      {hasChildren ? (
        <button onClick={onToggle} className="p-0.5 hover:bg-[hsl(var(--surface))] rounded text-[#6B7280]"
          data-testid={`org-node-toggle-${node.id}`}>
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>
      ) : (
        <div className="w-5" />
      )}
      <div className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: `${meta.color}20`, color: meta.color }}>
        <Icon size={12} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-white text-sm font-medium truncate">{node.name}</span>
          <Badge className="text-[9px] px-1.5 py-0" style={{ backgroundColor: `${meta.color}15`, color: meta.color, borderColor: `${meta.color}30` }}>
            {meta.label}
          </Badge>
          {node.locked_external && (
            <Badge className="text-[9px] px-1.5 py-0 bg-[#6B7280]/15 text-[#6B7280] border-[#6B7280]/30">External</Badge>
          )}
          {node.type === 'STAFF' && node.crm_user && (
            <Badge className="text-[9px] px-1.5 py-0 bg-[#10B981]/15 text-[#10B981] border-[#10B981]/30 inline-flex items-center gap-1">
              <Link2 size={8} /> {node.crm_user.name || node.crm_user.email}
            </Badge>
          )}
          {node.type === 'IB' && node.crm_ib && (
            <Badge className="text-[9px] px-1.5 py-0 bg-[#3B82F6]/15 text-[#3B82F6] border-[#3B82F6]/30 inline-flex items-center gap-1">
              <Link2 size={8} /> {node.crm_ib.name}
            </Badge>
          )}
        </div>
        {node.description && (
          <p className="text-[10px] text-[#6B7280] truncate">{node.description}</p>
        )}
      </div>
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
        {canHaveChildren && (
          <button onClick={() => onAdd(node)} title={`Add ${TYPE_META[meta.childType].label}`}
            data-testid={`org-node-add-child-${node.id}`}
            className="p-1.5 hover:bg-[#10B981]/15 rounded text-[#10B981]">
            <Plus size={12} />
          </button>
        )}
        <button onClick={() => onEdit(node)} title="Edit" data-testid={`org-node-edit-${node.id}`}
          className="p-1.5 hover:bg-[#3B82F6]/15 rounded text-[#3B82F6]">
          <Edit2 size={12} />
        </button>
        {(node.type === 'IB' || node.type === 'STAFF') && (
          <button onClick={() => onDuplicate(node)} title="Duplicate for another channel"
            data-testid={`org-node-duplicate-${node.id}`}
            className="p-1.5 hover:bg-[#F59E0B]/15 rounded text-[#F59E0B]">
            <Copy size={12} />
          </button>
        )}
        <button onClick={() => onDelete(node)} title="Delete" data-testid={`org-node-delete-${node.id}`}
          className="p-1.5 hover:bg-[#E63946]/15 rounded text-[#E63946]">
          <Trash2 size={12} />
        </button>
      </div>
    </div>
  );
}

function NodeEditModal({ open, mode, node, parent, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [users, setUsers] = useState([]);
  const [ibs, setIbs] = useState([]);
  const [saving, setSaving] = useState(false);
  const token = localStorage.getItem('token');
  const h = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!open) return;
    if (mode === 'add' && parent) {
      const childType = TYPE_META[parent.type]?.childType;
      setForm({ type: childType, name: '', description: '', parent_id: parent.id, weight: 0, crm_user_id: null, crm_ib_id: null });
    } else if (mode === 'add' && !parent) {
      setForm({ type: 'COMPANY', name: '', description: '', parent_id: null, weight: 0 });
    } else if (mode === 'edit' && node) {
      setForm({ ...node });
    }
  }, [open, mode, node, parent]);

  useEffect(() => {
    if (!open) return;
    if (form.type === 'STAFF') {
      axios.get(`${API}/scorecard/native/crm-users`, { headers: h }).then(r => {
        // Include current crm_user even if "linked" — so edit shows existing
        setUsers(r.data?.users || []);
      });
    } else if (form.type === 'IB') {
      axios.get(`${API}/scorecard/native/crm-ibs`, { headers: h }).then(r => {
        setIbs(r.data?.ibs || []);
      });
    }
    // eslint-disable-next-line
  }, [form.type, open]);

  const save = async () => {
    if (!form.name?.trim()) {
      toast.error('Name required');
      return;
    }
    setSaving(true);
    try {
      if (mode === 'add') {
        await axios.post(`${API}/scorecard/native/org-node`, form, { headers: h });
        toast.success('Node created');
      } else {
        await axios.put(`${API}/scorecard/native/org-node/${node.id}`, form, { headers: h });
        toast.success('Node updated');
      }
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const typeMeta = TYPE_META[form.type] || TYPE_META.STAFF;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[hsl(var(--surface))] border-[hsl(var(--border))] max-w-md" data-testid="org-node-edit-modal">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            {mode === 'add' ? <Plus size={16} /> : <Edit2 size={16} />}
            {mode === 'add' ? `Add ${typeMeta.label}` : `Edit ${typeMeta.label}`}
            {parent && <span className="text-xs text-[#6B7280]">under {parent.name}</span>}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-3">
          <div>
            <label className="text-xs text-[#9CA3AF] mb-1 block">Name *</label>
            <Input value={form.name || ''} onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="org-node-name-input"
              className="bg-transparent border-[hsl(var(--border))] text-white" autoFocus />
          </div>
          <div>
            <label className="text-xs text-[#9CA3AF] mb-1 block">Description</label>
            <Input value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="bg-transparent border-[hsl(var(--border))] text-white" />
          </div>
          <div>
            <label className="text-xs text-[#9CA3AF] mb-1 block">Weight (%)</label>
            <Input type="number" value={form.weight || 0} onChange={(e) => setForm({ ...form, weight: Number(e.target.value) })}
              className="bg-transparent border-[hsl(var(--border))] text-white" />
          </div>
          {form.type === 'STAFF' && (
            <div>
              <label className="text-xs text-[#9CA3AF] mb-1 block">Link to CRM User</label>
              <select
                value={form.crm_user_id || ''}
                onChange={(e) => setForm({ ...form, crm_user_id: e.target.value || null })}
                data-testid="org-node-crm-user-select"
                className="w-full bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] text-white text-sm rounded-md px-3 py-2"
              >
                <option value="">— Not linked —</option>
                {users.map(u => {
                  const otherNodes = (u.linked_to_node_names || []).filter(n => n !== form.name);
                  const linkedElsewhere = u.already_linked && otherNodes.length > 0 && u.id !== form.crm_user_id;
                  return (
                    <option key={u.id} value={u.id}>
                      {u.name || u.email}{linkedElsewhere ? ` (already linked: ${otherNodes.join(', ')})` : ''}
                    </option>
                  );
                })}
              </select>
              <p className="text-[10px] text-[#6B7280] mt-1">Bila linked, staff ni boleh log in ke CRM dan tengok scorecard sendiri di &quot;My Scorecard&quot;. User yang dah linked ke node lain pun masih boleh dipilih (mapping akan auto-move).</p>
            </div>
          )}
          {form.type === 'IB' && (
            <div>
              <label className="text-xs text-[#9CA3AF] mb-1 block">Link to CRM Channel</label>
              <select
                value={form.crm_ib_id || ''}
                onChange={(e) => setForm({ ...form, crm_ib_id: e.target.value || null })}
                data-testid="org-node-crm-ib-select"
                className="w-full bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] text-white text-sm rounded-md px-3 py-2"
              >
                <option value="">— Not linked —</option>
                {ibs.map(i => {
                  const otherNodes = (i.linked_to_node_names || []).filter(n => n !== form.name);
                  const linkedElsewhere = i.already_linked && otherNodes.length > 0 && i.id !== form.crm_ib_id;
                  return (
                    <option key={i.id} value={i.id}>
                      {i.name}{linkedElsewhere ? ` (already linked: ${otherNodes.join(', ')})` : ''}
                    </option>
                  );
                })}
              </select>
              <p className="text-[10px] text-[#6B7280] mt-1">Bila linked, KPI seperti Revenue, Active Client, dll auto-pull dari Daily Data CRM.</p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="org-node-save-btn"
            className="bg-gradient-to-r from-[#10B981] to-[#3B82F6] text-white">
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NodeDuplicateModal({ open, node, onClose, onSaved }) {
  const [form, setForm] = useState({ name: '', description: '', crm_user_id: null, crm_ib_id: null, copy_targets_from_period: '' });
  const [users, setUsers] = useState([]);
  const [ibs, setIbs] = useState([]);
  const [saving, setSaving] = useState(false);
  const token = localStorage.getItem('token');
  const h = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!open || !node) return;
    const d = new Date();
    const defaultPeriod = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    setForm({
      name: `${node.name} (copy)`,
      description: node.description || '',
      crm_user_id: null,
      crm_ib_id: null,
      copy_targets_from_period: defaultPeriod,
    });
    if (node.type === 'STAFF') {
      axios.get(`${API}/scorecard/native/crm-users`, { headers: h }).then(r => setUsers(r.data?.users || []));
    } else if (node.type === 'IB') {
      axios.get(`${API}/scorecard/native/crm-ibs`, { headers: h }).then(r => setIbs(r.data?.ibs || []));
    }
    // eslint-disable-next-line
  }, [open, node]);

  const save = async () => {
    if (!form.name?.trim()) { toast.error('Nama node baru diperlukan'); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description,
        crm_user_id: node.type === 'STAFF' ? form.crm_user_id : undefined,
        crm_ib_id: node.type === 'IB' ? form.crm_ib_id : undefined,
        copy_targets_from_period: form.copy_targets_from_period || undefined,
      };
      const r = await axios.post(`${API}/scorecard/native/org-node/${node.id}/duplicate`, payload, { headers: h });
      toast.success(`Duplicated: ${r.data?.node?.name} (${r.data?.copied_entries || 0} targets copied)`);
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Duplicate failed');
    } finally { setSaving(false); }
  };

  if (!node) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[hsl(var(--surface))] border-[hsl(var(--border))] max-w-md" data-testid="org-node-duplicate-modal">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Copy size={16} />
            Duplicate &quot;{node.name}&quot; untuk channel lain
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-3">
          <div className="bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded-md p-3 text-[11px] text-[#F59E0B]">
            <strong>Tip:</strong> Untuk staff/IB yang jaga lebih dari 1 channel (contoh &quot;Abang G&quot; jaga CIKO SS + Nusatrade), buat 1 node per channel supaya target & achievement boleh tracking berasingan.
          </div>
          <div>
            <label className="text-xs text-[#9CA3AF] mb-1 block">Nama baru *</label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="duplicate-node-name-input"
              placeholder="e.g. Abang G - Nusatrade"
              className="bg-transparent border-[hsl(var(--border))] text-white" autoFocus />
          </div>
          <div>
            <label className="text-xs text-[#9CA3AF] mb-1 block">Description</label>
            <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="e.g. Channel: Revolusi Nusatrade"
              className="bg-transparent border-[hsl(var(--border))] text-white" />
          </div>
          {node.type === 'IB' && (
            <div>
              <label className="text-xs text-[#9CA3AF] mb-1 block">Link to CRM channel *</label>
              <select
                value={form.crm_ib_id || ''}
                onChange={(e) => setForm({ ...form, crm_ib_id: e.target.value || null })}
                data-testid="duplicate-node-crm-ib-select"
                className="w-full bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] text-white text-sm rounded-md px-3 py-2"
              >
                <option value="">— Pick channel lain —</option>
                {ibs.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
              </select>
              <p className="text-[10px] text-[#6B7280] mt-1">Pick channel CRM yang LAIN dari original (supaya track separately).</p>
            </div>
          )}
          {node.type === 'STAFF' && (
            <div>
              <label className="text-xs text-[#9CA3AF] mb-1 block">Link to CRM User</label>
              <select
                value={form.crm_user_id || ''}
                onChange={(e) => setForm({ ...form, crm_user_id: e.target.value || null })}
                data-testid="duplicate-node-crm-user-select"
                className="w-full bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] text-white text-sm rounded-md px-3 py-2"
              >
                <option value="">— Not linked —</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="text-xs text-[#9CA3AF] mb-1 block">Copy targets from period (optional)</label>
            <Input value={form.copy_targets_from_period}
              onChange={(e) => setForm({ ...form, copy_targets_from_period: e.target.value })}
              placeholder="YYYY-MM (kosongkan untuk skip)"
              data-testid="duplicate-node-period-input"
              className="bg-transparent border-[hsl(var(--border))] text-white" />
            <p className="text-[10px] text-[#6B7280] mt-1">Kalau ada, semua target untuk period ni akan di-copy ke node baru.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="duplicate-node-save-btn"
            className="bg-gradient-to-r from-[#F59E0B] to-[#E63946] text-white">
            {saving ? 'Duplicating...' : 'Duplicate'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminScorecardOrgTab() {
  const [nodes, setNodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(new Set());
  const [modalState, setModalState] = useState({ open: false, mode: 'add', node: null, parent: null });
  const [duplicateState, setDuplicateState] = useState({ open: false, node: null });
  const token = localStorage.getItem('token');
  const h = { Authorization: `Bearer ${token}` };

  const loadTree = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/scorecard/native/org-tree`, { headers: h });
      setNodes(r.data?.nodes || []);
      // Auto-expand COMPANY + PM nodes initially
      const initExpanded = new Set();
      (r.data?.nodes || []).forEach(n => {
        if (n.type === 'COMPANY' || n.type === 'PM') initExpanded.add(n.id);
      });
      setExpanded(initExpanded);
    } catch (e) {
      toast.error('Gagal load org tree');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadTree(); }, []);

  const runMigration = async () => {
    if (!window.confirm('Migrate dari external snapshot ke native collection? Akan upsert tanpa overwrite admin edits.')) return;
    setMigrating(true);
    try {
      const r = await axios.post(`${API}/scorecard/native/migrate-from-snapshot`, {}, { headers: h });
      toast.success(`Migrated: ${JSON.stringify(r.data.migrated)}`);
      await loadTree();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Migration failed');
    } finally {
      setMigrating(false);
    }
  };

  const runAutoMatch = async () => {
    setMigrating(true);
    try {
      // Dry run first
      const r1 = await axios.post(`${API}/scorecard/native/auto-match`, { apply: false }, { headers: h });
      const matches = r1.data?.matches || [];
      if (matches.length === 0) {
        toast.info('No good matches found. Set links manually via Edit on each node.');
        return;
      }
      const proceed = window.confirm(
        `Found ${matches.length} suggested matches (similarity ≥ 75%):\n\n` +
        matches.slice(0, 10).map(m => `• ${m.node_type} "${m.node_name}" → "${m.matched_name}" (${(m.score * 100).toFixed(0)}%)`).join('\n') +
        (matches.length > 10 ? `\n... +${matches.length - 10} more` : '') +
        '\n\nApply all?'
      );
      if (!proceed) return;
      const r2 = await axios.post(`${API}/scorecard/native/auto-match`, { apply: true }, { headers: h });
      toast.success(`Auto-linked ${r2.data?.applied} nodes`);
      await loadTree();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Auto-match failed');
    } finally {
      setMigrating(false);
    }
  };

  const handleDelete = async (node) => {
    const children = nodes.filter(n => n.parent_id === node.id);
    let cascade = false;
    if (children.length > 0) {
      if (!window.confirm(`"${node.name}" ada ${children.length} child node. Delete semua sekali (cascade)?`)) return;
      cascade = true;
    } else {
      if (!window.confirm(`Delete "${node.name}"?`)) return;
    }
    try {
      await axios.delete(`${API}/scorecard/native/org-node/${node.id}?cascade=${cascade}`, { headers: h });
      toast.success(`Deleted ${node.name}`);
      await loadTree();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  // Build tree structure (children grouped by parent_id)
  const tree = useMemo(() => {
    const byParent = {};
    nodes.forEach(n => {
      const p = n.parent_id || 'ROOT';
      if (!byParent[p]) byParent[p] = [];
      byParent[p].push(n);
    });
    Object.values(byParent).forEach(arr => arr.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0) || a.name.localeCompare(b.name)));
    return byParent;
  }, [nodes]);

  // Filter visible nodes based on search
  const matchesSearch = (node) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (node.name || '').toLowerCase().includes(s) ||
           (node.description || '').toLowerCase().includes(s) ||
           (node.crm_user?.name || '').toLowerCase().includes(s);
  };

  // Render tree recursively
  const renderTree = (parentId = 'ROOT', depth = 0) => {
    const children = tree[parentId] || [];
    return children.map(node => {
      const isExpanded = expanded.has(node.id);
      const childrenList = tree[node.id] || [];
      const hasChildren = childrenList.length > 0;
      // Show this node if it matches OR has matching descendant
      const visibleChildren = renderTree(node.id, depth + 1);
      const showThis = matchesSearch(node) || (search && visibleChildren.length > 0);
      if (!showThis) return null;
      return (
        <React.Fragment key={node.id}>
          <NodeRow
            node={node}
            depth={depth}
            expanded={isExpanded || !!search}
            onToggle={() => setExpanded(prev => {
              const next = new Set(prev);
              next.has(node.id) ? next.delete(node.id) : next.add(node.id);
              return next;
            })}
            onAdd={(p) => setModalState({ open: true, mode: 'add', node: null, parent: p })}
            onEdit={(n) => setModalState({ open: true, mode: 'edit', node: n, parent: null })}
            onDelete={handleDelete}
            onDuplicate={(n) => setDuplicateState({ open: true, node: n })}
            hasChildren={hasChildren}
          />
          {(isExpanded || search) && visibleChildren}
        </React.Fragment>
      );
    }).filter(Boolean);
  };

  const stats = useMemo(() => {
    const counts = {};
    nodes.forEach(n => { counts[n.type] = (counts[n.type] || 0) + 1; });
    return counts;
  }, [nodes]);

  return (
    <div className="space-y-4" data-testid="admin-scorecard-org-tab">
      {/* Header / stats */}
      <div className="bg-gradient-to-br from-[#10B981]/10 to-[#3B82F6]/5 border border-[#10B981]/20 rounded-xl p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <Building2 size={20} className="text-[#10B981] mt-0.5" />
            <div>
              <h3 className="text-white text-base font-semibold mb-1">Scorecard Organization</h3>
              <p className="text-xs text-[#9CA3AF]">Edit org tree, link staff/channel ke CRM. Semua perubahan disimpan dalam CRM (bukan external link lagi).</p>
              <div className="flex flex-wrap gap-2 mt-2">
                {['COMPANY', 'PM', 'IB', 'STAFF'].map(t => {
                  const meta = TYPE_META[t];
                  return (
                    <Badge key={t} className="text-[10px]" style={{ backgroundColor: `${meta.color}15`, color: meta.color, borderColor: `${meta.color}30` }}>
                      {meta.label}: {stats[t] || 0}
                    </Badge>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={runAutoMatch} disabled={migrating} size="sm" variant="outline"
              data-testid="run-auto-match-btn"
              className="border-[#10B981]/30 text-[#10B981] text-xs">
              <Wand2 size={12} className="mr-1" />
              Auto-match
            </Button>
            <Button onClick={runMigration} disabled={migrating} size="sm" variant="outline"
              data-testid="run-migration-btn"
              className="border-[hsl(var(--border))] text-xs">
              <RefreshCw size={12} className={`mr-1 ${migrating ? 'animate-spin' : ''}`} />
              Sync from External
            </Button>
            <Button onClick={loadTree} disabled={loading} size="sm" variant="ghost"
              className="text-[#9CA3AF] hover:text-white">
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            </Button>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search node by name, description, linked user..."
            data-testid="org-tree-search"
            className="pl-9 bg-transparent border-[hsl(var(--border))] text-white text-sm h-9" />
        </div>
        <Button onClick={() => setModalState({ open: true, mode: 'add', node: null, parent: null })}
          size="sm" data-testid="org-tree-add-company-btn"
          className="bg-gradient-to-r from-[#10B981] to-[#3B82F6] text-white">
          <Plus size={14} className="mr-1" />Add Company
        </Button>
      </div>

      {/* Empty / loading */}
      {loading && nodes.length === 0 && (
        <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl p-8 text-center">
          <RefreshCw size={20} className="animate-spin mx-auto text-[#6B7280] mb-2" />
          <p className="text-sm text-[#9CA3AF]">Loading tree...</p>
        </div>
      )}

      {!loading && nodes.length === 0 && (
        <div className="bg-[#F59E0B]/5 border border-[#F59E0B]/20 rounded-xl p-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="text-[#F59E0B]" size={20} />
            <div>
              <h4 className="text-white font-semibold">Belum ada org node</h4>
              <p className="text-xs text-[#9CA3AF] mt-1">Click "Sync from External" untuk migrate dari scorecard-builder, atau "Add Company" untuk start fresh.</p>
            </div>
          </div>
        </div>
      )}

      {/* Tree */}
      {nodes.length > 0 && (
        <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl py-2">
          {renderTree('ROOT', 0)}
        </div>
      )}

      {/* Modal */}
      <NodeEditModal
        open={modalState.open}
        mode={modalState.mode}
        node={modalState.node}
        parent={modalState.parent}
        onClose={() => setModalState({ open: false, mode: 'add', node: null, parent: null })}
        onSaved={loadTree}
      />
      <NodeDuplicateModal
        open={duplicateState.open}
        node={duplicateState.node}
        onClose={() => setDuplicateState({ open: false, node: null })}
        onSaved={loadTree}
      />
    </div>
  );
}
